<template>
    <b-col class="p-0 h-100" cols="12">
        <b-card :bg-variant="Variant + '-light'" body-class="py-2" class="h-100">
            <b-card-title class="d-flex">
                <b-col cols="8" class="d-flex p-0 align-items-center">
                    <component :is="IconName" class="pr-2"/>
                    <h6 class="m-0 font-weight-semibold">{{ $t(TitleKey) }}</h6>
                </b-col>
                <b-col cols="4" class="d-flex justify-content-end">
                    <slot name="top-action"/>
                </b-col>
            </b-card-title>
            <template v-if="Resources && Resources.length > 0">
                <b-row 
                    v-for="(resource, index) in displayedResources" 
                    :key="index" 
                    class="mb-2 align-items-center"
                    @mouseenter="hoveredIndex = index"
                    @mouseleave="hoveredIndex = null">
                    <b-col cols="1">
                        <slot name="left-resource-action" :resource="resource" :index="index"></slot>
                    </b-col>
                    <b-col cols="8" class="text-truncate font-weight-bold" :title="`${resource.Name}.${resource.Extension}`">
                        <font-awesome-icon 
                            :icon="getFileIcon(resource.Extension)" 
                            class="mr-3" 
                            :class="'text-' + Variant" 
                            style="font-size: 1.2rem; width: 20px;" />
                        {{ resource.Name }}
                    </b-col>
                    <b-col 
                        cols="2" 
                        class="d-flex justify-content-end"
                        :style="{ visibility: hoveredIndex === index ? 'visible' : 'hidden' }">
                        <slot name="right-resource-action" :resource="resource" :index="index"></slot>
                    </b-col>
                </b-row>
            </template>
            <b-row v-else class="mb-2 align-items-center">
                <b-col cols="12" class="text-center text-muted">
                    {{ $t('NoAppendixes') }}
                </b-col>
            </b-row>
            <b-col class="d-flex justify-content-start p-0 mt-2">
                <b-button
                    v-if="Resources.length > MaxResources"
                    @click="displayAll = true"
                    :variant="'outline-' + Variant"
                    pill
                    size="sm"
                    class="mt-2"
                    v-b-tooltip.hover
                    :title="$t('ShowAll')">
                    <font-awesome-icon icon="fa-solid fa-folder-open" class="pr-2"/>
                    {{ $t('ShowAll') }} ({{ Resources.length }})
                </b-button>
                <slot name="bottom-action"/>
            </b-col>
        </b-card>
        <b-modal
            v-model="displayAll"
            :title="$t(TitleKey)"
            size="lg"
            ok-only
            :ok-title="$t('Close')"
            :ok-variant="Variant"
            scrollable>
            <b-row
                v-for="(resource, index) in Resources"
                :key="'modal-' + index"
                class="mb-2 align-items-center"
                @mouseenter="hoveredIndex = index"
                @mouseleave="hoveredIndex = null">
                <b-col cols="1">
                    <slot name="left-resource-action" :resource="resource" :index="index"></slot>
                </b-col>
                <b-col cols="8" class="text-truncate font-weight-bold" :title="`${resource.Name}.${resource.Extension}`">
                    <font-awesome-icon 
                        :icon="getFileIcon(resource.Extension)" 
                        class="mr-3" 
                        :class="'text-' + Variant" 
                        style="font-size: 1.2rem; width: 20px;" />
                    {{ resource.Name }}
                </b-col>
                <b-col 
                    cols="2" 
                    class="d-flex justify-content-end"
                    :style="{ visibility: hoveredIndex === index ? 'visible' : 'hidden' }">
                    <slot name="right-resource-action" :resource="resource" :index="index"></slot>
                </b-col>
            </b-row>
            <template #modal-footer>
                <b-button pill :variant="Variant" @click="displayAll = false">
                    {{ $t('Close') }}
                </b-button>
            </template>
        </b-modal>
    </b-col>
</template>

<script lang="ts">
    import { Component, Prop } from 'vue-property-decorator';
    import locale from '../../locales/SMA.json';
    
    // 1. On importe la classe mère
    import BaseCard from './BaseCard'; 
    import { Resource } from '@/types/Administration/SMA/SMACertificate';

    @Component({
        i18n: { messages: locale }
    })
    // 2. On étend BaseCard au lieu de Vue
    export default class ResourceCard extends BaseCard {
        /**
         * Propriété spécifique à CE composant
         */
        @Prop({ default: () => [] })
        public Resources!: Array<Resource>; 

        /**
         * Nombre maximum de documents affichés dans la carte
         */
        @Prop({ default: 5 })
        public MaxResources!: number;

        /**
         * Affichage de la fenêtre contenant l'ensemble des documents
         */
        private displayAll: boolean = false;

        /**
         * Documents affichés dans la carte (limit au nombre maximum)
         */
        public get displayedResources(): Array<Resource> {
            return this.Resources.slice(0, this.MaxResources);
        }
        
        /**
         * Index de la ressource actuellement survolée
         */
        private hoveredIndex: number | null = null;
            
        /**
         * Retourne l'icône FontAwesome correspondante à l'extension du fichier
         */
        private getFileIcon(extension: string): string {
            switch (extension) {
                case '.pdf':
                    return 'fa-solid fa-file-pdf';
                case '.csv':
                    return 'fa-solid fa-file-csv';
                case '.xlsx':
                case '.xls':
                    return 'fa-solid fa-file-excel';
                case '.txt':
                    return 'fa-solid fa-file-lines';
                case '.jpg':
                case '.jpeg':
                case '.png':
                case '.gif':
                case '.svg':
                case '.webp':
                    return 'fa-solid fa-file-image';
                default:
                    return 'fa-solid fa-file-alt';
            }
        }
    }
</script>